import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import { useTeam } from '@/hooks/useTeam'
import { useNavigate } from 'react-router-dom'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Loading from '@/components/common/Loading'
import Badge from '@/components/common/Badge'
import UpgradePrompt from '@/components/common/UpgradePrompt'

function FileManager() {
  const { team, loading: teamLoading } = useTeam()
  const navigate = useNavigate()
  
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(null)
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [editingFileId, setEditingFileId] = useState(null)
  const [editingFileName, setEditingFileName] = useState('')
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (team?.id) {
      fetchFiles()
    }
  }, [team?.id])

  const fetchFiles = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('files')
        .select('*')
        .eq('team_id', team.id)
        .order('uploaded_at', { ascending: false })

      if (error) throw error

      setFiles(data || [])
    } catch (err) {
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files || [])
    if (selectedFiles.length === 0) return

    // Check if free tier
    if (team.season_tier === 'free') {
      setShowUpgradePrompt(true)
      e.target.value = ''
      return
    }

    // Check file size (25MB limit per file for Pro tier)
    const maxSize = 25 * 1024 * 1024 // 25MB
    const oversizedFiles = selectedFiles.filter(file => file.size > maxSize)
    if (oversizedFiles.length > 0) {
      toast.error(`File size exceeds 25MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`)
      e.target.value = ''
      return
    }

    setUploading(true)

    // Upload files as a promise for toast.promise
    const uploadPromise = Promise.all(
      selectedFiles.map(async (file) => {
        // Upload to Supabase Storage
        const fileExt = file.name.split('.').pop()
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
        const filePath = `${team.id}/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from('team-files')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          })

        if (uploadError) throw uploadError

        // Save file metadata to database
        const { data: fileRecord, error: dbError } = await supabase
          .from('files')
          .insert({
            team_id: team.id,
            name: file.name,
            file_path: filePath,
            file_type: file.type || 'application/octet-stream',
            file_size: file.size,
            is_location_sensitive: false // Defaults to false/private (not public)
          })
          .select()
          .single()

        if (dbError) throw dbError

        return fileRecord
      })
    )

    toast.promise(uploadPromise, {
      loading: `Uploading ${selectedFiles.length} file(s)...`,
      success: (uploadedFiles) => {
        setFiles(prev => [...uploadedFiles, ...prev])
        e.target.value = '' // Reset input
        return `Successfully uploaded ${uploadedFiles.length} file(s)!`
      },
      error: (err) => {
        e.target.value = ''
        return `Failed to upload: ${err.message}`
      },
    })

    uploadPromise.finally(() => {
      setUploading(false)
    })
  }

  const handleDeleteFile = async (fileId, storagePath, fileName) => {
    const confirmed = await confirmDialog(
      `Delete "${fileName}"?`,
      { 
        confirmText: 'Delete', 
        cancelText: 'Cancel',
        description: 'This action cannot be undone.'
      }
    )
    if (!confirmed) return

    setDeleting(fileId)

    const deletePromise = (async () => {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('team-files')
        .remove([storagePath])

      if (storageError) {
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('files')
        .delete()
        .eq('id', fileId)
        .eq('team_id', team.id)

      if (dbError) throw dbError

      return fileName
    })()

    toast.promise(deletePromise, {
      loading: 'Deleting file...',
      success: (name) => {
        setFiles(prev => prev.filter(f => f.id !== fileId))
        return `Deleted ${name}`
      },
      error: (err) => `Failed to delete: ${err.message}`,
    }).finally(() => {
      setDeleting(null)
    })
  }

  const handleTogglePrivacy = async (fileId, currentPrivacy) => {
    const newPrivacy = !currentPrivacy

    const updatePromise = supabase
      .from('files')
      .update({ is_location_sensitive: newPrivacy })
      .eq('id', fileId)
      .eq('team_id', team.id)

    toast.promise(updatePromise, {
      loading: 'Updating privacy...',
      success: () => {
        setFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, is_location_sensitive: newPrivacy } : f
        ))
        return `File is now ${newPrivacy ? 'public' : 'private'}`
      },
      error: (err) => `Failed to update: ${err.message}`,
    })
  }

  const handleStartEdit = (fileId, currentName) => {
    setEditingFileId(fileId)
    setEditingFileName(currentName)
  }

  const handleCancelEdit = () => {
    setEditingFileId(null)
    setEditingFileName('')
  }

  const handleSaveFileName = async (fileId) => {
    if (!editingFileName.trim()) {
      toast.error('File name cannot be empty')
      return
    }

    const updatePromise = supabase
      .from('files')
      .update({ name: editingFileName.trim() })
      .eq('id', fileId)
      .eq('team_id', team.id)

    toast.promise(updatePromise, {
      loading: 'Updating file name...',
      success: () => {
        setFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, name: editingFileName.trim() } : f
        ))
        setEditingFileId(null)
        setEditingFileName('')
        return 'File name updated!'
      },
      error: (err) => `Failed to update: ${err.message}`,
    })
  }

  const handleDownloadFile = async (storagePath, fileName) => {

    const downloadPromise = (async () => {
      const { data, error } = await supabase.storage
        .from('team-files')
        .createSignedUrl(storagePath, 60) // 60 second expiry

      if (error) throw error

      // Trigger download
      const a = document.createElement('a')
      a.href = data.signedUrl
      a.download = fileName
      a.click()

      return fileName
    })()

    toast.promise(downloadPromise, {
      loading: 'Preparing download...',
      success: (name) => `Downloading ${name}`,
      error: (err) => `Failed to download: ${err.message}`,
    })
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  if (teamLoading || loading) {
    return <Loading text="Loading files..." />
  }

  if (!team) {
    navigate('/team-setup')
    return null
  }

  const isFree = team.season_tier === 'free'

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Bar */}
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              className="text-sm"
            >
              ← Back to Dashboard
            </Button>
          </div>

          <Card>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-bold">
                  Team Files
                </h1>
                {isFree && <Badge variant="amber">Pro Feature</Badge>}
              </div>
              {!isFree && (
                <div>
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    multiple
                    onChange={handleFileUpload}
                    disabled={uploading}
                    ref={fileInputRef}
                  />
                  <Button
                    variant="primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="cursor-pointer"
                  >
                    {uploading ? 'Uploading...' : '+ Upload Files'}
                  </Button>
                </div>
              )}
            </div>

            {!isFree && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900 mb-2">
                  <strong>Upload files like:</strong> Practice plans, tournament schedules, team photos, permission slips, uniform orders, sponsor info, etc.
                </p>
                <p className="text-xs text-blue-800">
                  25MB limit per file • Multiple files can be uploaded at once • Files are private by default
                </p>
              </div>
            )}

            {isFree ? (
              <div className="py-12 text-center">
                <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h2 className="font-heading text-xl font-bold mb-2">
                  File Uploads Available on Pro
                </h2>
                <p className="text-brand-slate mb-6">
                  Upgrade to Pro to upload and share files with your team (up to 5GB storage).
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate('/pricing')}
                >
                  Upgrade to Pro - $49/season
                </Button>
              </div>
            ) : (
              <>
                {uploading && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Uploading file...
                    </p>
                  </div>
                )}

                {files.length === 0 ? (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-brand-slate mb-4">
                      No files uploaded yet.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="cursor-pointer"
                    >
                      Upload Your First File
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-white border border-border rounded-lg hover:shadow-sm transition"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <svg className="w-8 h-8 text-brand-slate flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            {editingFileId === file.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={editingFileName}
                                  onChange={(e) => setEditingFileName(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveFileName(file.id)
                                    if (e.key === 'Escape') handleCancelEdit()
                                  }}
                                  className="flex-1 px-2 py-1 text-sm border border-brand-green rounded focus:outline-none focus:ring-2 focus:ring-brand-green"
                                  autoFocus
                                />
                              </div>
                            ) : (
                              <p className="font-medium text-brand-charcoal truncate">
                                {file.name}
                              </p>
                            )}
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              <span className="text-xs text-brand-slate">
                                {formatFileSize(file.file_size || 0)}
                              </span>
                              <span className="text-xs text-brand-slate">
                                {new Date(file.uploaded_at).toLocaleDateString()}
                              </span>
                              {file.is_location_sensitive ? (
                                <Badge variant="green">Public</Badge>
                              ) : (
                                <Badge variant="gray">Private</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap sm:flex-shrink-0">
                          {editingFileId === file.id ? (
                            <>
                              <Button
                                variant="primary"
                                onClick={() => handleSaveFileName(file.id)}
                                className="text-sm px-3 py-1.5"
                              >
                                Save
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={handleCancelEdit}
                                className="text-sm px-3 py-1.5"
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="secondary"
                                onClick={() => handleStartEdit(file.id, file.name)}
                                className="text-sm px-3 py-1.5"
                              >
                                ✏️ Rename
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() => handleTogglePrivacy(file.id, file.is_location_sensitive)}
                                className="text-sm px-3 py-1.5"
                              >
                                {file.is_location_sensitive ? '🔓 Make Private' : '🔒 Make Public'}
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() => handleDownloadFile(file.file_path, file.name)}
                                className="text-sm px-3 py-1.5"
                              >
                                Download
                              </Button>
                              <Button
                                variant="secondary"
                                onClick={() => handleDeleteFile(file.id, file.file_path, file.name)}
                                disabled={deleting === file.id}
                                className="text-sm px-3 py-1.5 text-red-600 border-red-300 hover:bg-red-50"
                              >
                                {deleting === file.id ? 'Deleting...' : 'Delete'}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {files.length > 0 && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-900">
                      <strong>Privacy Control:</strong> Files are private by default. Click the 🔒 button to make individual files public. Then enable "Files Public" in Portal Privacy settings to show them on your portal.
                    </p>
                  </div>
                )}
              </>
            )}
          </Card>

          {showUpgradePrompt && (
            <UpgradePrompt
              title="File Uploads are a Pro Feature"
              message="Upgrade to Pro for $49/season to upload and share files with your team (up to 5GB storage)."
              onUpgrade={() => navigate('/pricing')}
              onClose={() => setShowUpgradePrompt(false)}
            />
          )}
        </div>
      </Container>
    </div>
  )
}

export default FileManager


